import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9so3658o1vn02ekuir0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  const testimonials = [
    {
      name: '张先生',
      role: '影视爱好者',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20portrait%20headshot%2C%20simple%20warm%20background%2C%20friendly%20expression%2C%20clean%20lighting%2C%20corporate%20style&width=80&height=80&seq=avatar-01&orientation=squarish',
      content: '久久视频的内容更新速度真的很快，热门电影上线第一时间就能看到。画质清晰，播放流畅，已经推荐给身边所有朋友了！',
      rating: 5,
    },
    {
      name: '李女士',
      role: '综艺达人',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20portrait%20headshot%2C%20simple%20warm%20background%2C%20friendly%20smile%2C%20clean%20lighting%2C%20corporate%20style&width=80&height=80&seq=avatar-02&orientation=squarish',
      content: '分类特别清晰，找综艺节目很方便。而且完全免费，不用注册就能看，体验太好了。每天下班回家必刷久久视频。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '动漫迷',
      avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20student%20portrait%20headshot%2C%20casual%20style%2C%20simple%20warm%20background%2C%20friendly%20expression%2C%20clean%20lighting&width=80&height=80&seq=avatar-03&orientation=squarish',
      content: '作为一名动漫爱好者，久久视频的动漫资源真的太丰富了！国漫、日漫应有尽有，画质也很棒，完全是动漫迷的天堂。',
      rating: 5,
    },
  ];

  const milestones = [
    { year: '2020', title: '久久视频正式上线', desc: '怀着让更多人享受优质视频内容的初心，久久视频正式面向用户开放。' },
    { year: '2021', title: '用户突破500万', desc: '凭借优质内容和流畅体验，久久视频获得广泛认可，注册用户快速增长。' },
    { year: '2022', title: '内容库突破5000部', desc: '持续扩充内容资源库，涵盖影视、综艺、动漫、短视频等多种类型。' },
    { year: '2023', title: '月活用户突破3000万', desc: '成为国内领先的免费视频平台之一，日活跃用户持续攀升。' },
    { year: '2024', title: '全面升级品牌体验', desc: '品牌视觉焕新，功能全面升级，为用户带来更优质的视频观看体验。' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '久久视频',
    url: 'https://www.jiujiu-video.com/',
    description: '久久视频提供海量高清视频免费在线播放，涵盖影视、综艺、动漫、短视频等多种类型。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jiujiu-video.com',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiujiu-video.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Page Header */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <nav aria-label="面包屑导航" className="mb-6">
                <ol className="flex items-center gap-2 text-xs text-foreground-500">
                  <li>
                    <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                      首页
                    </Link>
                  </li>
                  <li>
                    <i className="ri-arrow-right-s-line"></i>
                  </li>
                  <li className="text-foreground-950 font-medium">关于我们</li>
                </ol>
              </nav>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                关于久久视频
              </h1>
              <p className="mt-3 text-sm text-foreground-600 max-w-2xl">
                致力于为用户提供海量高清视频内容，让每个人都能轻松享受精彩的视频世界
              </p>
            </div>
          </section>

          {/* Brand Intro */}
          <section className="py-14 md:py-20 bg-background-50" id="brand">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      久久视频成立于2020年，是一家专注于提供<strong>高清视频免费在线观看</strong>服务的互联网平台。
                      我们秉承"让每个人都能享受优质视频内容"的使命，汇聚了海量影视、综艺、动漫、短视频等多元内容，
                      致力于打造中国领先的免费视频聚合平台。
                    </p>
                    <p>
                      久久视频的名字寓意"长长久久"，我们希望每一位用户都能在这里找到属于自己的快乐，
                      让久久视频成为您生活中长久相伴的娱乐伙伴。从最初的小团队发展到如今服务数千万用户的平台，
                      我们始终不忘初心，坚持以用户需求为导向，持续优化产品体验。
                    </p>
                    <p>
                      久久视频的核心优势在于<strong>内容丰富、播放流畅、完全免费</strong>。
                      我们采用先进的视频编码和CDN加速技术，确保每一位用户都能享受到高清流畅的观看体验。
                      无论是热门大片、人气综艺，还是国漫新番、创意短视频，久久视频都能满足您的需求。
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-6 mt-8">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-500">5000万+</div>
                      <div className="text-xs text-foreground-500 mt-1">月活跃用户</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-accent-500">12,000+</div>
                      <div className="text-xs text-foreground-500 mt-1">视频资源</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-500">99.9%</div>
                      <div className="text-xs text-foreground-500 mt-1">服务可用率</div>
                    </div>
                  </div>
                </div>
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20lighting%2C%20collaborative%20workspace%2C%20Chinese%20tech%20company%20vibe%2C%20minimalist%20design%2C%20professional%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="久久视频团队"
                    title="久久视频 - 品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  发展历程
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  从零到一，久久视频的成长足迹
                </p>
              </div>
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden lg:block -translate-x-1/2"></div>
                <div className="space-y-8">
                  {milestones.map((item, idx) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col lg:flex-row gap-6 items-start ${
                        idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                        <div className={`${idx % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'}`}>
                          <span className="inline-block px-3 py-1 text-xs font-bold text-background-50 dark:text-foreground-950 bg-accent-500 rounded-full mb-2">
                            {item.year}
                          </span>
                          <h3 className="text-base font-semibold text-foreground-950">{item.title}</h3>
                          <p className="mt-1 text-sm text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden lg:flex items-center justify-center flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-accent-500 ring-4 ring-accent-100"></div>
                      </div>
                      <div className="flex-1 hidden lg:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-14 md:py-20 bg-background-50" id="reputation">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  来自真实用户的高度评价
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, idx) => (
                  <div
                    key={idx}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-14 md:py-20 bg-background-100" id="contact">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  有任何问题或建议，欢迎随时与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-medium text-foreground-950">电子邮箱</h3>
                  <p className="mt-1 text-xs text-foreground-500">contact@jiujiu-video.com</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-phone-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-medium text-foreground-950">客服电话</h3>
                  <p className="mt-1 text-xs text-foreground-500">400-888-9999</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-medium text-foreground-950">工作时间</h3>
                  <p className="mt-1 text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5 p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 text-sm font-medium text-background-50 dark:text-foreground-950 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '发送消息'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                    <i className="ri-check-line mr-1"></i>
                    消息已成功发送！我们会尽快与您联系，感谢您的反馈。
                  </div>
                )}

                {formStatus === 'error' && formError && (
                  <div className="p-4 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}